import { useState, useEffect, useContext } from 'react';
import { AppContext } from '../../../context/AppContext';
import { Card, Row, Col, Form, Button, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Wallet } from 'lucide-react';
import CurrencyDisplay from '../../../component/wallet/CurrencyDisplay';
import LoadingSpinner from '../../../component/wallet/LoadingSpinner';
import { useWallets } from '../../../Hooks/wallet/usewallets';

const topUpFormSchema = z.object({
    amount: z.coerce.number().positive('Enter a valid positive amount'),
    description: z.string().optional().or(z.literal('')),
    reference_id: z.string().optional().or(z.literal('')),
    reference_type: z.string().optional().or(z.literal('')),
});

const TopUpPage = () => {
    const { sidebar } = useContext(AppContext);
    const { loading, doTopUp, fetchWallets } = useWallets();
    const [wallet, setWallet] = useState(null);
    const [walletLoading, setWalletLoading] = useState(true);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({ resolver: zodResolver(topUpFormSchema) });

    useEffect(() => {
        const load = async () => {
            try {
                const res = await fetchWallets();
                const wallets = res?.data || [];
                if (wallets.length > 0) setWallet(wallets[0]);
            } catch {
                // handled
            } finally {
                setWalletLoading(false);
            }
        };
        load();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const refreshWallet = async () => {
        try {
            const res = await fetchWallets();
            const wallets = res?.data || [];
            if (wallets.length > 0) setWallet(wallets[0]);
        } catch {
            // handled
        }
    };

    const onSubmit = async (data) => {
        if (!wallet) return;
        try {
            await doTopUp({ ...data, currency_code: wallet.currency_code });
            reset();
            await refreshWallet();
        } catch {
            // handled by hook
        }
    };

    if (walletLoading) return <LoadingSpinner fullPage />;

    return (
        <div className={sidebar ? "taskMain " : "cx-active taskMain"} id="cx-main">
            <div className="role-content-main">
            <div className="sub-header-role mb-4">
                <h6 className="mt-2 role-text">Top Up</h6>
            </div>
                <div className="fade-in">
            

            {/* Wallet Info */}
            {wallet && (
                <Card className="border-0 shadow-sm mb-4" style={{ maxWidth: 600 }}>
                    <Card.Body>
                        <div className="d-flex align-items-center gap-2 mb-3 text-muted small fw-semibold text-uppercase">
                            <Wallet size={16} />
                            {wallet.currency_code} Wallet
                        </div>
                        <Row className="g-3">
                            <Col xs={6}>
                                <div className="text-muted small">Balance</div>
                                <CurrencyDisplay
                                    amount={wallet.balance}
                                    currencySymbol={wallet.currency_symbol}
                                    className="fw-semibold"
                                />
                            </Col>
                            <Col xs={6}>
                                <div className="text-muted small">Available</div>
                                <CurrencyDisplay
                                    amount={wallet.available_balance}
                                    currencySymbol={wallet.currency_symbol}
                                    className="fw-semibold"
                                />
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            )}

            {/* Top Up Form */}
            <Card className="border-0 shadow-sm" style={{ maxWidth: 600 }}>
                <Card.Body className="p-4">
                    {!wallet ? (
                        <p className="text-muted mb-0">No wallet found. Please contact support.</p>
                    ) : (
                        <Form onSubmit={handleSubmit(onSubmit)}>
                            <Form.Group className="mb-3">
                                <Form.Label>Amount ({wallet.currency_symbol}{wallet.currency_code})</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    placeholder="0.00"
                                    isInvalid={!!errors.amount}
                                    {...register('amount')}
                                />
                                <Form.Control.Feedback type="invalid">{errors.amount?.message}</Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Description <span className="text-muted fw-normal">(optional)</span></Form.Label>
                                <Form.Control type="text" placeholder="e.g., Manual top-up" {...register('description')} />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Reference ID <span className="text-muted fw-normal">(optional)</span></Form.Label>
                                <Form.Control type="text" placeholder="e.g., REF-12345" {...register('reference_id')} />
                            </Form.Group>

                            <Form.Group className="mb-4">
                                <Form.Label>Reference Type <span className="text-muted fw-normal">(optional)</span></Form.Label>
                                <Form.Select {...register('reference_type')}>
                                    <option value="">Select reference type</option>
                                    <option value="bank_transfer">Bank Transfer</option>
                                    <option value="check">Check</option>
                                    <option value="cash">Cash</option>
                                    <option value="other">Other</option>
                                </Form.Select>
                            </Form.Group>

                            <Button type="submit" variant="success" disabled={loading} className="w-100">
                                {loading ? <Spinner size="sm" /> : `Top Up ${wallet.currency_symbol}${wallet.currency_code}`}
                            </Button>
                        </Form>
                    )}
                </Card.Body>
            </Card>
        </div>
        </div>
        </div>
    );
};

export default TopUpPage;
